import { useQuery } from "@tanstack/react-query";
import { fetchRecommendations } from "@/services/supabaseService";
import { ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import ExpandableRecommendation from "./ExpandableRecommendation";

const Recommendations = () => {
  const { data: recommendations = [] } = useQuery({
    queryKey: ["recommendations"],
    queryFn: fetchRecommendations,
  });

  const getInitials = (name?: string) => {
    if (!name) return "";
    const normalized = name.trim().toLowerCase();
    if (normalized === "bivek bashyal") return "BB";
    if (normalized === "suresh bhattarai") return "SK"; // per request
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    const word = parts[0];
    const letters = (word || "").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
    return letters || (word?.[0] || "").toUpperCase();
  };

  return (
    <div id="recommendations" className="container max-w-6xl mx-auto py-20 px-6">
      <div className="animate-slide-down">
        <h2 className="text-4xl font-bold mb-2 text-teal">Recommendations</h2>
        <p className="text-gray-400 mb-6 break-words">
          Kind words and endorsements from colleagues, mentors, and clients.
        </p>
      </div>

      <div className="space-y-6">
        {recommendations.slice(0, 2).map((rec, index) => (
          <div
            key={rec.id}
            className="w-full glass-morphism rounded-xl p-6 flex items-start gap-4 animate-fade-in group transition-all duration-300 hover:scale-[1.01] hover:shadow-xl hover:shadow-teal/20"
            style={{ animationDelay: `${0.15 + index * 0.1}s` }}
          >
            <div className="flex-none">
              {rec.avatar_url ? (
                <img
                  src={rec.avatar_url}
                  alt={`${rec.author_name} avatar`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <span className="text-base font-semibold text-teal tracking-wide">
                    {getInitials(rec.author_name)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-3">
                <h3 className="text-white font-medium truncate">{rec.author_name}</h3>
                {(rec.author_role || rec.author_company) && (
                  <p className="text-sm text-gray-400 truncate">
                    {[rec.author_role, rec.author_company].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <ExpandableRecommendation content={rec.content} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-10">
        <Button className="bg-teal hover:bg-teal-dark text-navy-dark" asChild>
          <Link to="/recommendations" aria-label="View all recommendations">
            <span className="inline-flex items-center gap-2">View More <ArrowRight className="h-4 w-4" /></span>
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default Recommendations;
